// The Asset library for client accounts (HD-10, DESIGN rev 2.1 §3.3 Portal). Light by default with the toggle (the tier
// comes from AppShell's PortalRail), low density, the assets as Portal List rows. No Grid, no Expanded-row panel, no
// bulk actions (the old page gave client accounts none). A row's name opens the record in a dialog (AssetDialog).
//
// What a client account can do, with the old page's calls (useAssetLibrary, AssetUpload, AssetRecord):
//   browse and search   GET /api/assets (their clients' assets only, the API scopes it), folders, prop type, client
//   upload              images and ZIPs, when the account has an upload role (Editor on client accounts today)
//   in the record       Download; notes, Archive (upload roles); Delete (Editor, the API checks who uploaded it)
// Not here (staff only): moving between folders, folder admin, tag editing, re-examination, bulk delete.
import { Download, Image as ImageIcon, Search, Upload, X } from 'lucide-react';
import { useState } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { PROP_TYPES, fmtDate, plural, propLabel, userCanUpload } from '../../lib/assetLibrary';
import { AssetDialog, analysisBadge } from '../../components/AssetRecord';
import Button from '../../ui/Button';
import EmptyState from '../../ui/EmptyState';
import ErrorState from '../../ui/ErrorState';
import Icon from '../../ui/Icon';
import Input from '../../ui/Input';
import ListRow from '../../ui/ListRow';
import Select from '../../ui/Select';
import Skeleton from '../../ui/Skeleton';
import { useToast } from '../../ui/Toast';
import { useMedia, usePortalRailTier } from '../../shell/AppShell';
import { usePageHeader } from '../../shell/pageHeader';
import { PortalTierToggle } from '../../shell/portalTier';
import { clean } from '../../shell/copy';
import { useAssetLibrary } from './useAssetLibrary';
import { useAssetUpload } from './AssetUpload';

export default function PortalAssets() {
  const { user } = useAuth();
  const toast = useToast();
  const tier = usePortalRailTier();
  const phone = useMedia('(max-width: 600px)');
  const canUp = userCanUpload(user);
  const lib = useAssetLibrary();
  const [openId, setOpenId] = useState(null);
  const upload = useAssetUpload({
    clients: lib.clients, folderId: lib.folderId, folderName: lib.folderName, portal: true,
    onAnalysed: lib.refresh, onZipDone: lib.refresh,
  });

  async function download(a) {
    try {
      const r = await api.get(`/api/assets/${a.id}/download`);
      const link = document.createElement('a');
      link.href = r.data.url; link.download = a.fileName || 'asset'; link.click();
    } catch (err) { toast(err.response?.data?.error || 'The download did not start. Try again.', { tone: 'danger' }); }
  }

  usePageHeader({
    title: 'Asset library',
    meta: lib.state === 'data' ? `${plural(lib.total, 'asset')}${lib.filtered ? ' match' : ''}` : undefined,
    actions: (
      <>
        {canUp && <Button variant="primary" portal icon={Upload} data-testid="assets-upload" onClick={upload.openUpload}>Upload</Button>}
        {tier && <PortalTierToggle tier={tier.tier} toggle={tier.toggle} />}
      </>
    ),
  });

  const rows = lib.searchType === 'semantic_unavailable' ? [] : lib.assets;
  const empty = lib.searchType === 'semantic_unavailable'
    ? { message: 'No asset matches that search. Try other words.' }
    : lib.filtered
      ? { message: 'No asset matches these filters. Try a broader word, or clear the filters.', action: { label: 'Clear filters', icon: X, onClick: lib.clearFilters } }
      : lib.folderId ? { message: 'This folder is empty.' }
        : { message: canUp ? 'Your library is empty. Upload prop photos to start it.' : 'Your library is empty. The team adds the props for your shoots here.',
            ...(canUp ? { action: { label: 'Upload', icon: Upload, onClick: upload.openUpload } } : {}) };

  return (
    <div className="ui ui-page ui-page--portal">
      <div className="ui-toolbar">
        <Input className="ui-search" aria-label="Search your assets" icon={Search} type="search" placeholder="Search props, brands, themes" data-testid="assets-search"
          value={lib.searchInput} onChange={e => lib.setSearchInput(e.target.value)} />
        {lib.folders.length > 0 && (
          <Select aria-label="Folder" value={lib.folderId || ''} data-testid="assets-folder" onChange={e => lib.setFolderId(e.target.value || null)}
            options={[{ value: '', label: 'All assets' }, ...lib.folders.map(f => ({ value: f.id, label: clean(f.name) }))]} />
        )}
        <Select aria-label="Prop type" value={lib.propType} data-testid="assets-type-select" onChange={e => lib.setPropType(e.target.value)}
          options={[{ value: '', label: 'All types' }, ...PROP_TYPES.map(t => ({ value: t, label: propLabel(t) }))]} />
        {lib.clients.length > 1 && (
          <Select aria-label="Client" value={lib.clientId} onChange={e => lib.setClientId(e.target.value)}
            options={[{ value: '', label: 'All clients' }, ...lib.clients.map(c => ({ value: c.id, label: clean(c.name) }))]} />
        )}
        {lib.filtered && <Button variant="quiet" portal icon={X} onClick={lib.clearFilters}>Clear filters</Button>}
      </div>

      <section className="ui-section" aria-label="Assets">
        {lib.searchType === 'semantic' && lib.state === 'data' && <p className="ui-section__note">No exact match, so these are the most similar images.</p>}
        {lib.state === 'error' ? (
          <ErrorState title="Your assets did not load" message="Check your connection, then try again." onRetry={lib.load} />
        ) : lib.state === 'loading' ? (
          <div className="ui-list ui-region" aria-busy="true">{[0, 1, 2, 3].map(i => <Skeleton key={i} variant="block" height="80px" />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState {...empty} />
        ) : (
          <div className="ui-list ui-region">
            {rows.map(a => {
              const name = clean(a.fileName);
              const type = propLabel(clean(a.aiTags?.prop_type));
              return (
                <ListRow key={a.id} portal data-key={a.id}
                  leading={a.thumbnailUrl
                    ? <img className="ui-thumb ui-thumb--photo" src={a.thumbnailUrl} alt="" loading="lazy" />
                    : <span className="ui-thumb ui-thumb--photo ui-thumb--label"><Icon icon={ImageIcon} size={20} /></span>}
                  title={<button type="button" className="ui-link ui-wrapany" data-testid="assets-open" onClick={() => setOpenId(a.id)}>{name}</button>}
                  meta={(
                    <span className="ui-cluster">
                      {type && <span>{type}</span>}
                      {a.clientName && lib.clients.length > 1 && <span>{clean(a.clientName)}</span>}
                      <span>{fmtDate(a.createdAt)}</span>
                    </span>
                  )}
                  trailing={(
                    <>
                      {analysisBadge(a)}
                      <Button variant="secondary" portal icon={Download} iconOnly={phone} label={`Download ${name}`} aria-label={`Download ${name}`}
                        data-testid="assets-row-download" onClick={() => download(a)}>Download</Button>
                    </>
                  )} />
              );
            })}
          </div>
        )}
        {lib.hasMore && lib.state === 'data' && (
          <div className="ui-cluster">
            <Button variant="secondary" portal loading={lib.loadingMore} data-testid="assets-more" onClick={lib.loadMore}>Load more</Button>
            <span className="ui-meta">Showing {lib.assets.length} of {lib.total}</span>
          </div>
        )}
      </section>

      {upload.element}

      {openId && (
        <AssetDialog assetId={openId} user={user} folders={lib.folders} portal
          onClose={() => setOpenId(null)} onArchived={lib.refresh} onDeleted={lib.refresh} onFolderChanged={lib.refresh} />
      )}
    </div>
  );
}
